import { useRutilusQuery } from '../../../api/graphql/useRutilusQuery';
import { FishingWaterCard } from '../../../components/Cards/FishingWaterCard/FishingWaterCard';
import { ContinuousCarousel } from '../../../components/Carousels/ContinuousCarousel/ContinuousCarousel';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { Container } from '../../../components/Container/Container';
import { useGeolocation } from '../../../contexts/GeolocationContext/GeolocationContext';
import { useClientHasRendered } from '../../../helpers/hooks';
import {
  GetSpeciesSuggestedWater,
  GetSpeciesSuggestedWaterVariables,
} from '../../../interfaces/graphql';
import { getSpeciesSuggestedWater } from '../../../queries/rutilus/Species';

interface IProps {
  speciesId: string;
  speciesName: string;
}

export const NearbyWatersSection = ({ speciesId, speciesName }: IProps) => {
  const { isLoading: currentLocationLoading, location } = useGeolocation();

  const { data, loading } = useRutilusQuery<
    GetSpeciesSuggestedWater,
    GetSpeciesSuggestedWaterVariables
  >(getSpeciesSuggestedWater, {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    variables: { id: speciesId, ...location?.coordinates! },
    skip: currentLocationLoading,
  });
  const clientHasRendered = useClientHasRendered();

  const suggestedWaters = data ? data.entry.suggestedWaters : [];
  const filteredWaters = suggestedWaters
    .filter(water => water.catches.totalCount > 0)
    .sort((a, b) => b.catches.totalCount - a.catches.totalCount);

  if (!loading && filteredWaters.length === 0) {
    return null;
  }

  return (
    <div data-testid="NearbyWatersSection">
      <Container>
        <ContinuousCarousel
          dataTestId="nearby-waters"
          title={`Top fishing spots near you to catch ${speciesName}`}
          description={`These are the top places near you to catch ${speciesName} based on live data from Fishbrain.`}
          showContentLoader
          contentLoaderKey="nearby-waters"
          isLoading={!clientHasRendered || loading}
        >
          {filteredWaters.map((water, index) => (
            <FishingWaterCard
              key={`nearby-water-${water._id}`}
              imageWidth={372}
              latitude={water.latitude}
              longitude={water.longitude}
              clickEventSource="species-nearby-waters"
              name={water.displayName}
              waterId={water._id}
              clickEventPosition={index}
              clickEventSourceId={speciesId}
              countryName={water.country?.countryName}
              regionName={water.region?.name ?? undefined}
              numberOfCatches={water.catches.totalCount}
            />
          ))}
        </ContinuousCarousel>
        <div className="my-10 flex justify-end">
          <InternalLink to="/explore" variant="buttonPrimary" size="sm">
            Explore full map
          </InternalLink>
        </div>
      </Container>
    </div>
  );
};
